import React from 'react';
import styledImport, { ThemeProvider } from 'styled-components';
import type { ThemedStyledInterface } from 'styled-components';
import { Breakpoints, Color, Grid, Type } from '../../app/StyleGuide';
import windowService from '../../services/windowService';
import { limitText } from '../../utils/textUtils';
import { useLogin, useUser } from '../authentication';
import ProfileImage from '../../components/ProfileImage';
import { AuthPillButton } from './components/AuthPillButton';
import { BrandMark } from './components/BrandMark';
import NavbarMobileMenu from './components/MobileMenu';
import NavbarMoreMenu from './components/MoreMenu';
import useNavbar from './hooks/useNavbar';
import { getNavbarTheme, type NavbarTheme } from './state/Navbar.theme';

const styled = styledImport as ThemedStyledInterface<NavbarTheme>;

const NAVBAR_HEIGHT = '60px';

const StyledNavbar = styled.nav`
  margin: 0;
  height: ${NAVBAR_HEIGHT};
  max-height: ${NAVBAR_HEIGHT};
  border: none;
  border-bottom: none;
  background: ${({ theme }) => theme.background};
  color: ${({ theme }) => theme.textColor};
`;

const NavbarContainer = styled.div`
  position: relative;
  width: 100%;
  margin: 0;
  padding: 0;
  display: block;

  @media (min-width: ${Breakpoints.screen_sm}) {
    display: flex;
    align-items: stretch;
    padding-left: ${Grid._4};
    margin-left: auto;
    margin-right: auto;
  }

  @media (min-width: ${Breakpoints.screen_lg}) {
    width: 1170px;
  }
`;

const NavbarHeader = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  height: ${NAVBAR_HEIGHT};
  min-height: ${NAVBAR_HEIGHT};
  border-right: none;
  padding: 0 15px;

  @media (min-width: ${Breakpoints.screen_sm}) {
    border-right: 1px solid ${({ theme }) => theme.headerBorderColor};
    padding: 0;
  }
`;

const NavContent = styled.div`
  display: none;
  justify-content: space-between;
  flex: 1 1 auto;
  align-items: stretch;
  min-width: 0;

  @media (min-width: ${Breakpoints.screen_sm}) {
    display: flex;
  }
`;

const DesktopNavList = styled.ul`
  display: none;
  align-items: stretch;
  list-style: none;
  margin: 0;
  padding: 0;

  @media (min-width: ${Breakpoints.screen_sm}) {
    display: flex;
  }
`;

const MainNav = styled(DesktopNavList)`
  flex: 1 1 auto;
  min-width: 0;
`;

const NavTabButton = styled.button<{ $active?: boolean }>`
  appearance: none;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  width: 100%;
  height: ${NAVBAR_HEIGHT};
  margin: 0;
  border: 0;
  padding: ${Grid._4};
  background: transparent;
  font: inherit;
  text-align: left;
  cursor: pointer;
  line-height: ${Type.Leading.tall};
  color: inherit;
  text-decoration: none;
  text-transform: ${({ theme }) => theme.navTextTransform};
  letter-spacing: ${({ theme }) => theme.navLetterSpacing};
  font-size: ${({ theme }) => theme.navFontSize};
  font-weight: ${({ theme }) => theme.navFontWeight};
  white-space: nowrap;

  &:hover,
  &:focus {
    color: inherit;
    text-decoration: none;
  }

  i {
    margin-right: 5px;
    color: ${({ $active, theme }) => ($active ? theme.activeIconColor : theme.navIconColor)};
    opacity: ${({ $active, theme }) => ($active ? theme.activeIconOpacity : theme.navIconOpacity)};
    font-size: ${Type.Scale._2};
    transition: color 0.125s linear, opacity 0.125s linear;
  }
`;

const OrgItem = styled.li<{ $active: boolean; $stretch?: boolean }>`
  display: flex;
  flex: ${({ $stretch }) => ($stretch ? '1 1 auto' : '0 0 auto')};
  justify-content: center;
  background: ${({ $active, theme }) => ($active ? theme.activeBackground : 'transparent')};
  color: ${({ $active, theme }) => ($active ? theme.activeTextColor : theme.textColor)};
  transition: background-color 0.125s linear, color 0.125s linear;

  &:hover {
    background: ${({ $active, theme }) => ($active ? theme.activeBackground : theme.hoverBackground)};
  }
`;

const AccountNav = styled(DesktopNavList)`
  margin-left: auto;

  > li {
    border-left: 1px solid rgba(255, 255, 255, 0.08);
  }
`;

const ProfileImageThumbnail = styled.span`
  display: inline-flex;
  align-items: center;
  margin-right: 0;
  color: ${Color.Gray._30};
  background-color: ${Color.white};
  border-radius: 50%;

  @media (min-width: ${Breakpoints.screen_sm}) {
    margin-right: ${Grid._3};
  }
`;

const DashboardLabel = styled.span<{ $hideOnSmall: boolean }>`
  display: ${({ $hideOnSmall }) => ($hideOnSmall ? 'none' : 'inline-flex')};
  align-items: center;

  @media (min-width: ${Breakpoints.screen_md}) {
    display: inline-flex;
  }
`;

const DashboardName = styled.span`
  text-transform: ${({ theme }) => theme.dashboardNameTransform};
`;

const LoggedOutNav = styled.div`
  display: none;
  align-items: center;
  justify-content: flex-end;
  flex: 1 1 auto;
  margin-left: auto;
  margin-top: ${Type.Scale._05};
  margin-bottom: ${Type.Scale._05};

  @media (min-width: ${Breakpoints.screen_sm}) {
    display: flex;
  }
`;

function getOriginCode(): string {
  return window.getOrigin ? window.getOrigin() : 'GRID';
}

export default function Navbar({ activeTab, hideNavbar }: { activeTab?: string; hideNavbar: boolean }) {
  const { user } = useUser();
  const { redirectToLogin, redirectToRegister } = useLogin(false);

  const origin = getOriginCode();
  const navbarTheme = getNavbarTheme(origin);

  const loggedIn = Boolean(user?.userId);
  const hasPermission = (p: string) => (user?.permissions || []).indexOf(p) > -1;

  const onLoggedOutLogin = () => {
    const route = window.location.hash.replace(/^#/, '').split('?')[0] || '/';
    redirectToLogin(route.startsWith('/') ? route : `/${route}`);
  };

  const onLoggedOutRegister = () => redirectToRegister(window.location.href);

  const profileImageUrl = user?.profileImageUrl;

  const dashboardLabel = user?.firstName ? `${limitText(user.firstName, 16)}'s Dashboard` : 'Your Dashboard';
  const { primaryActions, moreActions, loggedOutActions, orgName, orgLogoUrl } = useNavbar({
    origin,
    activeTab,
    hideNavbar,
  });

  if (hideNavbar) {
    return null;
  }
  return (
    <ThemeProvider theme={navbarTheme}>
      <StyledNavbar role="navigation">
        <NavbarContainer>
          <NavbarHeader>
            <NavbarMobileMenu
              loggedIn={loggedIn}
              primaryActions={primaryActions}
              moreActions={moreActions}
              loggedOutActions={loggedOutActions}
              dashboardLabel={dashboardLabel}
              profileImageUrl={profileImageUrl}
              onLogin={onLoggedOutLogin}
              onRegister={onLoggedOutRegister}
            />
            <BrandMark
              orgLogoUrl={orgLogoUrl}
              loggedIn={loggedIn}
              orgName={orgName}
              keepOrgNameVisible={!user?.permissions?.length}
            />
          </NavbarHeader>

          {loggedIn && (
            <NavContent id="slidemenu">
              <MainNav data-qa-hook="menuView">
                {primaryActions.map(action => (
                  <OrgItem key={action.key} $active={!!action.active}>
                    <NavTabButton
                      type="button"
                      $active={!!action.active}
                      data-qa-hook={action.qaHook}
                      onClick={() => {
                        action.onSelect();
                      }}
                    >
                      <i className={action.iconClass} /> {action.label}
                    </NavTabButton>
                  </OrgItem>
                ))}
              </MainNav>
              <AccountNav>
                <OrgItem $active={activeTab === 'Account' || activeTab === 'Profile' || activeTab === 'Training'}>
                  <NavTabButton
                    type="button"
                    $active={activeTab === 'Account' || activeTab === 'Profile' || activeTab === 'Training'}
                    onClick={() => {
                      windowService.redirectTo('/dashboard');
                    }}
                  >
                    <ProfileImageThumbnail>
                      <ProfileImage src={profileImageUrl} size="30px" />
                    </ProfileImageThumbnail>
                    <DashboardLabel aria-label="Dashboard" $hideOnSmall={hasPermission('Account View')}>
                      {!user?.firstName && ' Your'}
                      {user?.firstName && (
                        <>
                          <DashboardName>{limitText(user.firstName, 10)}</DashboardName>
                          &rsquo;s Dashboard
                        </>
                      )}
                    </DashboardLabel>
                  </NavTabButton>
                </OrgItem>
                <NavbarMoreMenu actions={moreActions} />
              </AccountNav>
            </NavContent>
          )}

          {!loggedIn && (
            <LoggedOutNav>
              <AuthPillButton $variant="secondary" type="button" onClick={onLoggedOutRegister}>
                Register
              </AuthPillButton>
              <AuthPillButton $variant="primary" type="button" onClick={onLoggedOutLogin}>
                Log In
              </AuthPillButton>
            </LoggedOutNav>
          )}
        </NavbarContainer>
      </StyledNavbar>
    </ThemeProvider>
  );
}
